import { MenuButton } from './MenuButton';
import { UploadError, UploadErrorBar } from './UploadErrorBar';
import { UploadState, UploadStatusBar } from './UploadStatusBar';
import { CameraIcon } from '@raysee/icon';
import { FC, useEffect, useState } from 'react';
import ReactFileReader from 'react-file-reader';
import TextareaAutosize from 'react-textarea-autosize';
import Image from 'next/image';
import LineIcon from '../../public/line-icon.png';
import { CounselState, LINE_LINKS, LineAccount } from '@raysee/schema';
import { PencilIcon } from '@raysee/icon';

interface Props {
  enableFinish: boolean;
  defaultText?: string;
  disableCancel?: boolean;
  onSend: (text: string, isNotification?: boolean) => void;
  onUpload?: (file: File) => void;
  /** 画像アップロード中の状態。null/未指定なら表示しない */
  uploadState?: UploadState | null;
  onCancelUpload?: () => void;
  /** 画像アップロードの失敗。null/未指定なら表示しない(アップロード中は進捗の表示を優先する) */
  uploadError?: UploadError | null;
  onRetryUpload?: () => void;
  onDismissUploadError?: () => void;
  onClickOption?: () => void;
  onClickFinish?: () => void;
  onClickCancel?: () => void;
  onChangeText?: (text: string) => void;
  counselState: CounselState;
  lineAccount?: LineAccount;
}

export const ToolBar: FC<Props> = ({
  enableFinish,
  defaultText,
  disableCancel,
  onSend,
  onUpload,
  uploadState,
  onCancelUpload,
  uploadError,
  onRetryUpload,
  onDismissUploadError,
  onClickOption,
  onClickFinish,
  onClickCancel,
  onChangeText,
  counselState,
  lineAccount,
}) => {
  const [text, setText] = useState('');
  const [isTextEnabled, setTextEnabled] = useState(false);

  useEffect(() => {
    setText(defaultText);
  }, [defaultText]);

  const onClickCamera = (file: File[]) => {
    onUpload(file[0]);
  };

  const onClickSend = () => {
    if (text !== '') onSend(text, true);
    setText('');
    if (onChangeText) onChangeText('');
  };

  const onConfirm = () => {
    onSend('鑑定終了');
  };

  const textChanged = (text: string) => {
    setText(text);
    if (onChangeText) onChangeText(text);
  };

  const onClickEnableText = () => {
    setTextEnabled(true);
  };

  const showMenuButton = !!onClickOption || !!onClickFinish || !!onClickCancel;

  return !!counselState ? (
    counselState === CounselState.InputProblemDetail ? (
      isTextEnabled ? (
        <div className="fixed bottom-0 flex w-full max-w-[480px] transform flex-col justify-center bg-white p-2">
          <div className="aline-center my-1.5 flex items-center rounded-2xl border border-gray-300">
            <TextareaAutosize
              placeholder="メッセージ"
              value={text}
              maxRows={10}
              onChange={(e) => textChanged(e.currentTarget.value)}
              className="py-auto mx-3 w-full flex-1 px-4 outline-none"
            />
            <div className="flex items-end self-stretch">
              <a
                className={`text-brand px-4 py-3 font-semibold ${
                  text !== '' ? 'hover:cursor-pointer' : 'text-gray-300'
                }`}
                onClick={onClickSend}
              >
                送信
              </a>
            </div>
          </div>
        </div>
      ) : (
        <div className="flex items-center border-t bg-white p-2">
          <button
            onClick={onClickEnableText}
            className="text-brand mx-2 my-2 flex  flex-grow items-center justify-center rounded-md border border-[#ff66c4] py-2"
          >
            <PencilIcon />
            <span className="mx-2 font-bold">相談内容を入力</span>
          </button>
        </div>
      )
    ) : (
      <div className="fixed bottom-0 flex w-full max-w-[480px] transform flex-wrap justify-center bg-white px-2 py-1">
        <a
          className={`mx-2 my-2 flex flex-grow  items-center justify-center rounded-md border border-[#EBEBED] bg-[#22BA4F] py-2 font-bold`}
          href={LINE_LINKS[lineAccount?.provider]}
        >
          <Image src={LineIcon} width={28} height={28} alt="line-icon" />
          <span className="mx-2 font-bold text-white">友だち追加で通知を受け取る</span>
        </a>
      </div>
    )
  ) : (
    <>
      {uploadState ? (
        <UploadStatusBar uploadState={uploadState} onCancel={onCancelUpload} />
      ) : (
        uploadError && (
          <UploadErrorBar
            uploadError={uploadError}
            onRetry={onRetryUpload}
            onDismiss={onDismissUploadError}
          />
        )
      )}
      <div className="flex items-center border-t bg-white p-2">
        {enableFinish && (
          <a
            className="mr-3 rounded bg-gray-500 px-4 font-semibold text-white hover:cursor-pointer hover:text-white"
            style={{
              paddingTop: 6,
              paddingBottom: 6,
              fontSize: 12,
            }}
            onClick={onConfirm}
          >
            鑑定
            <br />
            終了
          </a>
        )}

        {showMenuButton && (
          <MenuButton
            disableCancel={disableCancel}
            onClickOption={onClickOption}
            onClickFinish={onClickFinish}
            onClickCancel={onClickCancel}
          />
        )}

        {onUpload && (
          <ReactFileReader handleFiles={onClickCamera} disabled={!!uploadState}>
            <div
              className={`mx-2 ${
                uploadState ? 'cursor-not-allowed opacity-40' : 'hover:cursor-pointer'
              }`}
            >
              <CameraIcon width="24" height="24" />
            </div>
          </ReactFileReader>
        )}
        <div className="mx-3 flex-1">
          <TextareaAutosize
            placeholder="メッセージを入力"
            value={text}
            maxRows={10}
            onChange={(e) => textChanged(e.currentTarget.value)}
            className="w-full rounded bg-gray-100 px-4 py-3"
          />
        </div>
        <a
          className="bg-brand rounded px-4 font-semibold text-white hover:cursor-pointer hover:text-white"
          style={{ paddingTop: 12, paddingBottom: 12 }}
          onClick={onClickSend}
        >
          送信
        </a>
      </div>
    </>
  );
};
